import React, { useState, useEffect, useRef } from 'react';
import api from '../api';
import { IconVoice, IconX } from './Icons';

function speak(text) {
  if (!window.speechSynthesis) return;
  window.speechSynthesis.cancel();
  const utt = new SpeechSynthesisUtterance(text);
  utt.lang = 'en-US';
  utt.rate = 1.0;
  window.speechSynthesis.speak(utt);
}

export default function VoiceInterface({ restaurantId, compact = false, onClose }) {
  const [listening, setListening] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [typed, setTyped] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [supported, setSupported] = useState(true);
  const recognitionRef = useRef(null);
  const transcriptRef = useRef('');

  useEffect(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) { setSupported(false); return; }

    const recognition = new SpeechRecognition();
    recognition.continuous = false;
    recognition.interimResults = true;
    recognition.lang = 'en-US';

    recognition.onresult = (e) => {
      const t = Array.from(e.results).map(r => r[0].transcript).join('');
      transcriptRef.current = t;
      setTranscript(t);
    };

    recognition.onend = async () => {
      setListening(false);
      if (transcriptRef.current) await queryVoice(transcriptRef.current);
    };

    recognitionRef.current = recognition;
  }, []);

  async function queryVoice(text) {
    setLoading(true);
    try {
      const { data } = await api.post('/webhooks/voice/query', {
        transcript: text,
        restaurant_id: restaurantId,
      });
      setResult(data.result);
      if (data.result?.message) speak(data.result.message);
    } catch {
      const msg = 'Sorry, I could not process that query.';
      setResult({ message: msg });
      speak(msg);
    } finally {
      setLoading(false);
    }
  }

  function toggleListen() {
    if (listening) {
      recognitionRef.current?.stop();
      setListening(false);
    } else {
      setTranscript('');
      transcriptRef.current = '';
      setTyped('');
      setResult(null);
      recognitionRef.current?.start();
      setListening(true);
    }
  }

  async function handleSubmitTyped(e) {
    e.preventDefault();
    if (!typed.trim()) return;
    setTranscript(typed);
    await queryVoice(typed);
  }

  if (!supported) {
    return (
      <div className="alert alert-warning">
        Voice recognition isn't supported in this browser. Try Chrome on desktop or Android.
      </div>
    );
  }

  return (
    <div className={compact ? 'voice-compact' : 'card'} style={compact ? {} : { maxWidth: 500, margin: '0 auto' }}>
      {!compact && <div className="card-header" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <IconVoice width={16} height={16} stroke="#0D9488" /> Voice Interface
      </div>}

      <div className={compact ? 'voice-compact-body' : 'card-body'} style={{ textAlign: 'center' }}>
        {compact && onClose && (
          <button onClick={onClose} style={{ position: 'absolute', top: 12, right: 12, background: 'none', border: 'none', cursor: 'pointer', color: '#64748b' }}>
            <IconX width={18} height={18} />
          </button>
        )}

        <p className="text-gray text-sm mb-4">
          Ask about inventory, what's below par, pending orders, or sales data.
        </p>

        <button
          className={`voice-orb${listening ? ' listening' : ''}`}
          onClick={toggleListen}
          title={listening ? 'Stop listening' : 'Tap to speak'}
        >
          <IconVoice width={28} height={28} stroke="#fff" strokeWidth={1.8} />
        </button>

        <p className="text-sm mt-3" style={{ color: listening ? '#ef4444' : '#9ca3af' }}>
          {listening ? 'Listening… tap to stop' : 'Tap to speak'}
        </p>

        {transcript && (
          <div className="mt-4" style={{ background: 'rgba(13,148,136,0.08)', borderRadius: 8, padding: '10px 14px', textAlign: 'left' }}>
            <div className="text-xs text-gray mb-1">You said:</div>
            <div style={{ color: '#e2e8f0' }}>"{transcript}"</div>
          </div>
        )}

        {loading && (
          <div className="text-sm mt-3" style={{ color: '#0D9488' }}>Thinking…</div>
        )}

        {result && !loading && (
          <div className="mt-4" style={{ textAlign: 'left' }}>
            <div style={{ background: 'rgba(13,148,136,0.06)', border: '1px solid rgba(13,148,136,0.2)', borderRadius: 8, padding: '12px 14px', color: '#e2e8f0', whiteSpace: 'pre-line', fontSize: 14, lineHeight: 1.6 }}>
              {result.message}
            </div>
            <button
              onClick={() => speak(result.message)}
              className="btn btn-ghost btn-sm mt-2"
              style={{ fontSize: 12 }}
            >
              <IconVoice width={12} height={12} /> Read again
            </button>
          </div>
        )}

        <form onSubmit={handleSubmitTyped} className="mt-4" style={{ textAlign: 'left', display: 'flex', gap: 8 }}>
          <input
            type="text"
            placeholder="Or type a question…"
            value={typed}
            onChange={e => setTyped(e.target.value)}
            style={{ flex: 1 }}
          />
          <button className="btn btn-primary btn-sm" type="submit" disabled={loading || !typed.trim()}>Ask</button>
        </form>

        <div className="mt-3 text-xs text-gray" style={{ textAlign: 'left', lineHeight: 1.7 }}>
          Try: "What's below par?" · "How much ahi do we have?" · "What orders need approval?"
        </div>
      </div>
    </div>
  );
}
